import { Box, Collapse } from '@mui/material';
import { EntityIdentifier } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import { TouchEvent, useCallback, useMemo, useState } from 'react';
import { ResolvedSummary } from 'src/components/Replies';
import {
  MessageReply,
  useMessageThread,
} from 'src/graphql/entities/conversation-message-reply';
import { useMessageDeepLink } from 'src/pages/Conversations/Chat/MessageDeepLink/message-deep-link-context';
import { useChatCommonData } from 'src/pages/Conversations/Chat/registry/common-data-context';
import { ReplyAvatar } from './reply-avatar';
import { ReplyComposer } from './reply-composer';
import { ReplyItem } from './reply-item';

interface MessageThreadProps {
  conversationMessageId: string;
  partnerId: number;
  readonly: boolean;
}

interface ReplyAuthor {
  id: string;
  name: string;
  partnerId?: number;
  avatarFileKey?: string | null;
}

function getDistinctAuthors(replies: MessageReply[]): ReplyAuthor[] {
  const authors = new Map<string, ReplyAuthor>();
  replies.forEach((reply) => {
    const user = reply.senderChannel?.conversationParticipant?.platformUser;
    if (user && !authors.has(user.userId)) {
      authors.set(user.userId, {
        id: user.userId,
        name: getFullName(user),
        partnerId: user.partnerId,
        avatarFileKey: user.avatarFileKey,
      });
    }
  });
  return [...authors.values()];
}

// Touches in the thread belong to the replies, not to the note card's long-press.
const stopTouchPropagation = (event: TouchEvent) => event.stopPropagation();

/**
 * The replies of an internal note and the box to add one. A resolved thread folds every reply
 * but the resolution into one expandable line and takes no new replies.
 */
export const MessageThread = ({
  conversationMessageId,
  partnerId,
  readonly,
}: MessageThreadProps) => {
  const { replies, resolutionId, resolveThread, unresolveThread } =
    useMessageThread(conversationMessageId);
  const submitReply = useChatCommonData((s) => s.submitReply);
  const [isExpanded, setIsExpanded] = useState(false);

  const resolution = resolutionId
    ? replies.find((reply) => reply.id === resolutionId)
    : undefined;
  const folded = useMemo(
    () => (resolution ? replies.filter((reply) => reply.id !== resolution.id) : []),
    [replies, resolution],
  );
  const foldedAuthors = useMemo(() => getDistinctAuthors(folded), [folded]);

  // a deep link to a reply folded behind the resolution unfolds the thread once, then the reader decides
  const highlightedFoldedId = useMessageDeepLink((s) =>
    folded.some((reply) => reply.id === s.highlightedMessageId)
      ? s.highlightedMessageId
      : null,
  );
  const [unfoldedForId, setUnfoldedForId] = useState<string | null>(null);
  if (highlightedFoldedId && highlightedFoldedId !== unfoldedForId) {
    setUnfoldedForId(highlightedFoldedId);
    setIsExpanded(true);
  }

  const handleToggle = useCallback(() => setIsExpanded((prev) => !prev), []);
  const handleSubmit = useCallback(
    (body: string, mentions: EntityIdentifier[]) =>
      submitReply({ parentConversationMessageId: conversationMessageId, body, mentions }),
    [conversationMessageId, submitReply],
  );

  if (!replies.length && readonly) {
    return null;
  }

  const renderReply = (reply: MessageReply, isResolution = false) => (
    <ReplyItem
      key={reply.id}
      replyId={reply.id}
      isResolution={isResolution}
      readonly={readonly}
      onResolve={resolveThread}
      onUnresolve={unresolveThread}
    />
  );

  return (
    <Box data-testid="message-thread" onTouchStart={stopTouchPropagation}>
      {resolution ? (
        <>
          {folded.length ? (
            <>
              <ResolvedSummary
                count={folded.length}
                authorNames={foldedAuthors.map((author) => author.name)}
                avatars={foldedAuthors.map((author) => (
                  <ReplyAvatar
                    key={author.id}
                    name={author.name}
                    partnerId={author.partnerId}
                    avatarFileKey={author.avatarFileKey}
                  />
                ))}
                expanded={isExpanded}
                onToggle={handleToggle}
              />
              <Collapse in={isExpanded}>{folded.map((reply) => renderReply(reply))}</Collapse>
            </>
          ) : null}
          {renderReply(resolution, true)}
        </>
      ) : (
        <>
          {replies.map((reply) => renderReply(reply))}
          {readonly ? null : <ReplyComposer partnerId={partnerId} onSubmit={handleSubmit} />}
        </>
      )}
    </Box>
  );
};
